import React, { useMemo, useState } from "react";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Grid from "components/common/Grid";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { usePhcTheme } from "../PhcThemeContext";
import {
  LIQUIDITY_CARD_THEMES,
  buildLiquiditySectionData,
} from "../liquiditySynopsisData";

const ActionBadge = ({ action, mobile }) => {
  const { theme } = usePhcTheme();
  if (!action) {
    return <Text sx={{ color: theme.textMuted }}>—</Text>;
  }
  const style = theme.actionStyles?.[action] || {
    color: theme.textSecondary,
    bg: theme.badgeBg,
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        px: mobile ? "13px" : 1,
        py: mobile ? "5px" : 0.75,
        borderRadius: "9999px",
        bgcolor: style.bg,
        border: mobile
          ? `1px solid ${style.color}4D`
          : "none",
      }}
    >
      <Text
        sx={{
          fontSize: mobile ? 10 : 12,
          fontWeight: 500,
          color: style.color,
          whiteSpace: "nowrap",
          lineHeight: mobile ? "15px" : 1.2,
          textTransform: mobile ? "uppercase" : "none",
        }}
      >
        {action}
      </Text>
    </Box>
  );
};

const RiskBar = ({ value, tone }) => {
  const { theme } = usePhcTheme();
  const color =
    tone === "negative"
      ? "#C40000"
      : tone === "warning"
        ? "#F6A823"
        : tone === "positive"
          ? "#45AC70"
          : "#2879D8";
  const pct = Math.max(0, Math.min(100, Number(value) || 0));

  if (value == null || value === "") {
    return <Text sx={{ color: theme.textMuted }}>—</Text>;
  }

  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: "100%" }}>
      <Box
        sx={{
          flex: 1,
          height: 6,
          borderRadius: 1,
          bgcolor: theme.badgeBg,
          overflow: "hidden",
          minWidth: 48,
        }}
      >
        <Box
          sx={{
            width: `${pct}%`,
            height: "100%",
            bgcolor: color,
            borderRadius: 1,
          }}
        />
      </Box>
      <Text
        sx={{
          fontSize: { xs: 13, md: 16 },
          color: theme.textPrimary,
          whiteSpace: "nowrap",
          minWidth: 48,
          textAlign: "right",
        }}
      >
        {Number(value).toFixed(1)}%
      </Text>
    </Stack>
  );
};

const MOBILE_SHORT_LABELS = {
  core: "CORE POSITIONS",
  supporting: "SUPPORTING",
  tail: "TAIL POSITIONS",
  noise: "NOISE POSITIONS",
  liquidity: "LIQUIDITY",
};

/** Mobile compact card — Figma 5626:89181 (horizontal scroll) */
const MobileSummaryCard = ({ card, selected, onSelect }) => {
  const { theme } = usePhcTheme();
  const cardTheme = LIQUIDITY_CARD_THEMES[card.themeKey] || LIQUIDITY_CARD_THEMES.core;
  const borderColor = cardTheme.mobileBorder || cardTheme.topBorder;
  const label = MOBILE_SHORT_LABELS[card.key] || card.label;
  const subtitle = (card.subtitle || "—").replace(/ · /g, " • ");

  return (
    <Box
      onClick={() => onSelect(card.key)}
      sx={{
        flex: "0 0 144px",
        width: 144,
        height: 129,
        borderRadius: "12px",
        border: `1px solid ${borderColor}`,
        bgcolor: theme.mobileCardBg,
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        px: "17px",
        pt: "18px",
        pb: "17px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        cursor: "pointer",
        boxShadow: selected
          ? "0px 0px 15px 0px rgba(0,194,203,0.2)"
          : "none",
        flexShrink: 0,
      }}
    >
      <Text
        sx={{
          fontWeight: 400,
          fontSize: 30,
          color: theme.textPrimary,
          lineHeight: "36px",
        }}
      >
        {card.value ?? "—"}
      </Text>
      <Text
        sx={{
          fontWeight: 400,
          fontSize: 10,
          color: theme.textMuted,
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          lineHeight: "15px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Text>
      <Text
        sx={{
          fontWeight: 400,
          fontSize: 9,
          color: theme.mobileLabelColor,
          lineHeight: "13.5px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {subtitle}
      </Text>
      <Stack
        direction="row"
        spacing="4px"
        alignItems="center"
        sx={{ mt: "auto", pt: "3.5px" }}
      >
        <Text
          sx={{
            fontSize: 9,
            color: "#00C2CB",
            lineHeight: "13.5px",
            whiteSpace: "nowrap",
          }}
        >
          View Detail
        </Text>
        {selected ? (
          <KeyboardArrowUpIcon sx={{ color: "#00C2CB", fontSize: 12 }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ color: "#00C2CB", fontSize: 12 }} />
        )}
      </Stack>
    </Box>
  );
};

const SummaryCard = ({ card, selected, onSelect }) => {
  const { theme } = usePhcTheme();
  const cardTheme = LIQUIDITY_CARD_THEMES[card.themeKey] || LIQUIDITY_CARD_THEMES.core;

  return (
    <Box
      onClick={() => onSelect(card.key)}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 240,
        borderRadius: theme.radiusCard,
        borderTop: `6px solid ${cardTheme.topBorder}`,
        backgroundImage: theme.sectionBg,
        backgroundColor: theme.cardBg,
        boxShadow: theme.cardShadow,
        overflow: "hidden",
        cursor: "pointer",
        px: 2,
        pt: 2.5,
        pb: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        outline: selected ? `1px solid ${cardTheme.topBorder}` : "none",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: theme.insetGlow,
        },
      }}
    >
      <Stack
        spacing={1.25}
        alignItems="center"
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          flex: 1,
          justifyContent: "flex-start",
        }}
      >
        <Text
          sx={{
            fontWeight: 700,
            fontSize: 42,
            color: theme.textPrimary,
            lineHeight: 1,
          }}
        >
          {card.value ?? "—"}
        </Text>
        <Text
          sx={{
            fontWeight: 500,
            fontSize: 14,
            color: theme.textPrimary,
            textAlign: "center",
            letterSpacing: 0.2,
            minHeight: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {card.label}
        </Text>
        <Text
          sx={{
            fontWeight: 400,
            fontSize: 13,
            color: theme.textPrimary,
            textAlign: "center",
            lineHeight: 1.35,
            minHeight: 40,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            px: 0.5,
          }}
        >
          {card.subtitle || "—"}
        </Text>
      </Stack>

      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        justifyContent="center"
        sx={{
          position: "relative",
          zIndex: 1,
          mt: "auto",
          pt: 1.5,
          width: "100%",
          flexShrink: 0,
        }}
      >
        <Text
          sx={{
            fontWeight: 400,
            fontSize: 15,
            color: "#2879D8",
            whiteSpace: "nowrap",
          }}
        >
          View in Detail
        </Text>
        {selected ? (
          <KeyboardArrowUpIcon sx={{ color: "#2879D8", fontSize: 22 }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ color: "#2879D8", fontSize: 22 }} />
        )}
      </Stack>
    </Box>
  );
};

const COLUMNS = [
  { key: "stock", label: "STOCK", flex: 1.1 },
  { key: "sector", label: "SECTOR", flex: 1.1 },
  { key: "weight", label: "WEIGHT", flex: 0.7 },
  { key: "value", label: "Value (₹)", flex: 1 },
  { key: "vsIdeal", label: "VS IDEAL*", flex: 1.2 },
  { key: "risk", label: "RISK CONTRIBUTION", flex: 1.4 },
  // { key: "action", label: "ACTION", flex: 0.8 },
];

const DesktopCell = ({ col, row, theme }) => {
  switch (col.key) {
    case "stock":
      return (
        <Text sx={{ fontSize: 15, fontWeight: 500, color: theme.textPrimary }}>
          {row.stock}
        </Text>
      );
    case "sector":
      return (
        <Text
          sx={{
            fontSize: 15,
            fontWeight: 500,
            color: theme.textPrimary,
            textTransform: "uppercase",
          }}
        >
          {row.sector}
        </Text>
      );
    case "weight":
      return (
        <Text sx={{ fontSize: 15, color: theme.textPrimary }}>
          {row.weightLabel}
        </Text>
      );
    case "value":
      return (
        <Text sx={{ fontSize: 15, color: theme.textPrimary }}>
          {row.valueLabel}
        </Text>
      );
    case "vsIdeal":
      return (
        <Text
          sx={{
            fontSize: 15,
            color:
              theme.vsIdealColors?.[row.vsIdealTone] ||
              theme.vsIdealColors?.neutral ||
              theme.textPrimary,
          }}
        >
          {row.vsIdeal}
        </Text>
      );
    case "risk":
      return (
        <RiskBar value={row.riskContribution} tone={row.riskTone} />
      );
    case "action":
      return <ActionBadge action={row.action} />;
    default:
      return null;
  }
};

/**
 * Liquidity (Tail Stock & Allocation Risk) — Figma 5917:3359
 * New UI shell; maps liquiditySynopsis + stockAllocation; missing keys → —
 */
const LiquiditySynopsisSection = ({
  liquidityData,
  stockAllocation = [],
  apiSection,
}) => {
  const { theme } = usePhcTheme();
  const model = useMemo(
    () =>
      buildLiquiditySectionData({
        liquidityData,
        stockAllocation,
        apiSection,
      }),
    [liquidityData, stockAllocation, apiSection],
  );

  const [selectedKey, setSelectedKey] = useState("core");

  const selectedCard =
    model.cards.find((c) => c.key === selectedKey) || model.cards[0];

  const rows = useMemo(
    () => model.buildRowsForCard(selectedCard),
    [model, selectedCard],
  );

  const insight = useMemo(
    () => model.buildInsight(selectedCard),
    [model, selectedCard],
  );

  const handleSelect = (key) => {
    setSelectedKey((prev) => (prev === key ? prev : key));
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: { xs: 0, md: theme.radiusSection },
        border: {
          xs: "none",
          md: `1px solid ${theme.sectionBorder}`,
        },
        backgroundImage: { xs: "none", md: theme.sectionBg },
        backgroundColor: { xs: "transparent", md: theme.cardBg },
        boxShadow: { xs: "none", md: theme.cardShadow },
        px: { xs: 0, md: 2.25 },
        py: { xs: 0, md: 3 },
        overflow: { xs: "visible", md: "hidden" },
        "&::after": {
          content: { xs: "none", md: '""' },
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: theme.sectionInsetGlow,
        },
      }}
    >
      <Text
        sx={{
          position: "relative",
          zIndex: 1,
          fontWeight: 600,
          fontSize: { xs: 24, md: 24 },
          color: { xs: theme.mobileValueColor, md: theme.textPrimary },
          lineHeight: { xs: "32px", md: 1.2 },
          mb: { xs: 2, md: 3 },
        }}
      >
        Liquidity ( Tail Stock & Allocation Risk)
      </Text>

      {/* Mobile: horizontal scroll cards — Figma 5626:89181 */}
      <Box
        sx={{
          display: { xs: "block", md: "none" },
          position: "relative",
          zIndex: 1,
          width: "105%",
          maxWidth: "105%",
          // Bleed so next card peeks past the right edge
          mr: "-5%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            gap: "12px",
            width: "100%",
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
            scrollSnapType: "x mandatory",
            pb: 0.5,
            pr: 1,
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {model.cards.map((card) => (
            <Box
              key={card.key}
              sx={{ scrollSnapAlign: "start", flexShrink: 0 }}
            >
              <MobileSummaryCard
                card={card}
                selected={selectedKey === card.key}
                onSelect={handleSelect}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Desktop: grid cards */}
      <Grid
        container
        spacing={1.5}
        sx={{
          display: { xs: "none", md: "flex" },
          position: "relative",
          zIndex: 1,
        }}
      >
        {model.cards.map((card) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={2.4}
            key={card.key}
            sx={{ display: "flex" }}
          >
            <SummaryCard
              card={card}
              selected={selectedKey === card.key}
              onSelect={handleSelect}
            />
          </Grid>
        ))}
      </Grid>

      {/* Insight / recommendation */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          mt: { xs: 2, md: 3 },
          bgcolor: {
            xs: theme.mobileCardBg,
            md: theme.insightBannerBg === "transparent" ? theme.cardBg : theme.insightBannerBg,
          },
          border: {
            xs: theme.mobileCardBorder,
            md: "none",
          },
          borderRadius: { xs: "12px", md: "8px" },
          px: { xs: "17px", md: 4 },
          py: { xs: 2, md: 1.5 },
        }}
      >
        <Text
          sx={{
            fontWeight: 400,
            fontSize: { xs: 13, md: 14 },
            color: { xs: theme.textSecondary, md: theme.textPrimary },
            lineHeight: 1.5,
          }}
        >
          {insight.body}{" "}
          <Box
            component="span"
            sx={{
              fontWeight: 600,
              fontSize: { xs: 14, md: 16 },
              color: theme.textPrimary,
              display: { xs: "block", md: "inline" },
              mt: { xs: 1.25, md: 0 },
            }}
          >
            {insight.recommendation}
          </Box>
        </Text>
      </Box>

      {/* Detail table (desktop) + mobile holding cards — Figma 5626:89225 */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          mt: { xs: 2, md: 3 },
        }}
      >
        {/* Mobile cards */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1, pt: 1 }}
          >
            <Text
              sx={{
                fontSize: 12,
                color: theme.mobileLabelColor,
                letterSpacing: "1.2px",
                textTransform: "uppercase",
                lineHeight: "16px",
              }}
            >
              Portfolio Holdings
            </Text>
            <Text
              sx={{
                fontSize: 10,
                color: "#00C2CB",
                lineHeight: "15px",
              }}
            >
              Sort by Weight ↓
            </Text>
          </Stack>

          {rows.length === 0 ? (
            <Box
              sx={{
                minHeight: 100,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme.textMuted,
                fontSize: 14,
              }}
            >
              No positions in this tier
            </Box>
          ) : (
            <Stack spacing={2}>
              {rows.map((row, index) => {
                const toneColor =
                  theme.vsIdealColors?.[row.vsIdealTone] ||
                  theme.vsIdealColors?.neutral ||
                  theme.textPrimary;
                const riskPct = Math.max(
                  0,
                  Math.min(100, Number(row.riskContribution) || 0),
                );
                return (
                  <Box
                    key={`${row.stock}-m-${index}`}
                    sx={{
                      borderRadius: "16px",
                      border: theme.mobileCardBorder,
                      bgcolor: theme.mobileCardBg,
                      backdropFilter: "blur(6px)",
                      p: "17px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      justifyContent="space-between"
                    >
                      <Box>
                        <Text
                          sx={{
                            fontSize: 16,
                            color: theme.textPrimary,
                            letterSpacing: "-0.4px",
                            lineHeight: "24px",
                          }}
                        >
                          {row.stock}
                        </Text>
                        <Text
                          sx={{
                            fontSize: 10,
                            color: theme.mobileLabelColor,
                            textTransform: "uppercase",
                            lineHeight: "15px",
                          }}
                        >
                          {row.sector || "—"}
                        </Text>
                      </Box>
                      <ActionBadge action={row.action} mobile />
                    </Stack>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Text
                          sx={{
                            fontSize: 10,
                            color: theme.mobileLabelColor,
                            lineHeight: "15px",
                            mb: "4px",
                          }}
                        >
                          Weight
                        </Text>
                        <Text
                          sx={{
                            fontSize: 14,
                            color: theme.textPrimary,
                            lineHeight: "20px",
                          }}
                        >
                          {row.weightLabel}
                        </Text>
                      </Box>
                      <Box>
                        <Text
                          sx={{
                            fontSize: 10,
                            color: theme.mobileLabelColor,
                            lineHeight: "15px",
                            mb: "4px",
                          }}
                        >
                          Value
                        </Text>
                        <Text
                          sx={{
                            fontSize: 14,
                            color: theme.textPrimary,
                            lineHeight: "20px",
                          }}
                        >
                          {row.valueLabel}
                        </Text>
                      </Box>
                    </Box>

                    <Stack spacing={1} sx={{ pt: 0.5 }}>
                      <Stack
                        direction="row"
                        alignItems="flex-end"
                        justifyContent="space-between"
                      >
                        <Text
                          sx={{
                            fontSize: 10,
                            color: theme.mobileLabelColor,
                            lineHeight: "15px",
                          }}
                        >
                          Risk Contribution
                        </Text>
                        <Text
                          sx={{
                            fontSize: 12,
                            color: toneColor,
                            lineHeight: "16px",
                          }}
                        >
                          {row.riskContribution != null &&
                          row.riskContribution !== ""
                            ? `${Number(row.riskContribution).toFixed(1)}%`
                            : "—"}
                        </Text>
                      </Stack>
                      <Box
                        sx={{
                          height: 6,
                          borderRadius: "9999px",
                          bgcolor: "rgba(255,255,255,0.05)",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${riskPct}%`,
                            height: "100%",
                            bgcolor: toneColor,
                            borderRadius: "9999px",
                          }}
                        />
                      </Box>
                      <Text
                        sx={{
                          fontSize: 10,
                          pt: 1,
                          lineHeight: "15px",
                        }}
                      >
                        <Box component="span" sx={{ color: toneColor }}>
                          {row.vsIdeal === "—" || !row.vsIdeal
                            ? "—"
                            : row.vsIdeal.replace(/\s*vs ideal$/i, "")}
                        </Box>
                        {row.vsIdeal && row.vsIdeal !== "—" && (
                          <Box component="span" sx={{ color: theme.textPrimary }}>
                            {" "}
                            vs ideal
                          </Box>
                        )}
                      </Text>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>

        {/* Desktop table */}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            borderRadius: theme.radiusCard,
            border: `1px solid ${theme.sectionBorder}`,
            backgroundImage: theme.sectionBg,
            backgroundColor: theme.cardBg,
            boxShadow: theme.cardShadow,
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              borderRadius: "inherit",
              pointerEvents: "none",
              boxShadow: theme.innerPanelInsetGlow,
            },
            position: "relative",
          }}
        >
          <Box sx={{ overflowX: "auto", position: "relative", zIndex: 1 }}>
            <Box sx={{ minWidth: 900 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minHeight: 55,
                  px: 3,
                  bgcolor: theme.tableHeaderSolid || "rgba(0,126,240,0.2)",
                }}
              >
                {COLUMNS.map((col) => (
                  <Box key={col.key} sx={{ flex: col.flex, px: 0.5 }}>
                    <Text
                      sx={{
                        fontSize: 16,
                        fontWeight: 500,
                        color: theme.tableHeaderText || theme.textPrimary,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {col.label}
                    </Text>
                  </Box>
                ))}
              </Box>

              {rows.length === 0 ? (
                <Box
                  sx={{
                    minHeight: 120,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: theme.textMuted,
                    fontSize: 14,
                  }}
                >
                  No positions in this tier
                </Box>
              ) : (
                rows.map((row, index) => (
                  <Box
                    key={`${row.stock}-${index}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: 68,
                      px: 3,
                      borderBottom:
                        index < rows.length - 1
                          ? `1px solid ${theme.sectionBorder}`
                          : "none",
                    }}
                  >
                    {COLUMNS.map((col) => (
                      <Box key={col.key} sx={{ flex: col.flex, px: 0.5 }}>
                        <DesktopCell col={col} row={row} theme={theme} />
                      </Box>
                    ))}
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LiquiditySynopsisSection;
